import { useState } from "react";
import { ArrowUp, Check, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Badge, Button } from "@hub/ui";
import type { ViewPrimitive, BladeContext } from "../../shell/registry";
import type { BladeDescriptor } from "../../shell/types";
import {
  BladeLoading,
  BladeError,
  BladeDenied,
  BladeEmpty,
  forcedBladeState,
} from "../../shell/BladeStates";
import { useAuditBatch } from "../AuditDataSource";
import { useTourNavigator } from "../help/TourProvider";
import {
  caseLabel,
  caseLabelTitle,
  isFallbackLabel,
} from "../data/caseIdentity";
import { coderIdentity } from "../data/coders";
import { faDate } from "../data/faDates";
import { useCodeEntry } from "../data/codebookSource";
import { codeTitle } from "../data/codeLabels";
import { loadSubmittedIds } from "../data/verdicts";
import type { AuditBatch, AuditBatchCaseSummary, CaseStatus } from "../types";
import {
  CASE_COLUMNS,
  CASE_GROUP_FIELDS,
  groupCases,
  sortCases,
  type CaseColumn,
  type CaseField,
  type CaseGroupField,
  type SortDirection,
} from "./caseTable";
import {
  BatchNumber,
  BatchStateBadge,
  CaseIdentifier,
  FacilityLabel,
  CaseStatusBadge,
  CoderAvatar,
  JobCodeChip,
  Progress,
  batchActionLabel,
} from "./parts";

// Batch-detail view-primitive (HUB-504). The drill target from the batch queue:
// a batch header (with a Start / Continue review action + a batch-complete banner)
// and the batch's case table. Each case row opens the case's side-by-side review
// (the audit-review compare blade) in the shell's focus presentation mode.
// Blind-safe: rows show a surgical-CPT A/B teaser + a discrepancy COUNT, both
// derived from opaque display slots — never which slot is the agent vs the coder.

/** Open a case's review (compare) blade in focus mode. Stops propagation so the
 *  blade's own click-to-activate doesn't fire afterwards and steal the active
 *  index back to this (parent) blade — which in focus mode would hide the child. */
function openReview(
  ctx: BladeContext,
  c: AuditBatchCaseSummary,
  e?: { stopPropagation: () => void },
): void {
  e?.stopPropagation();
  const descriptor: BladeDescriptor = {
    key: "audit-review",
    // The DCN. The blade FETCHES by this, so it must stay the key even though
    // the breadcrumb below names the case by its FA episode id.
    entityId: c.id,
    // NO eyebrow, and the bare case id as the title.
    //
    // The breadcrumb renders `eyebrow › title` per blade, so the batch blade
    // already contributes "AMGA › B-2026-0612-01". Repeating the job code here
    // produced "AMGA › B-2026-0612-01 › AMGA › Review AC-100482" — the client
    // twice and a verb where an identifier belongs. Dropping it leaves the chain
    // the reviewer actually navigates: job code › batch › episode.
    //
    // Safe because this blade is chromeless: the eyebrow had no other consumer,
    // and Breadcrumb guards on `blade.eyebrow &&`.
    //
    // The EPISODE ID, not the DCN: the breadcrumb is read by a person, and it
    // should name the case the same way the review header does.
    title: caseLabel(c),
    width: "xwide",
    // The compare screen renders its own merged header — suppress the shell blade-head.
    chromeless: true,
  };
  ctx.openChild(descriptor);
  ctx.setMode("focus");
}

/** One coded surgical CPT with the procedure it names. */
function SurgicalCode({ code }: { code: string }) {
  const lookup = useCodeEntry(code);
  return (
    <span className="flex flex-col leading-tight">
      <span className="font-mono text-xs text-foreground">{code}</span>
      <span className="max-w-[220px] truncate text-[10.5px] text-muted-foreground">
        {codeTitle(lookup)}
      </span>
    </span>
  );
}

/**
 * The blind A/B surgical teaser, with each code's procedure resolved.
 *
 * FA carries no procedure narrative, so this is where "what was done" actually
 * comes from: the CPT the two sides assigned, looked up in the server codebook.
 * That is why it could not be a plain Procedure column — the answer is per SLOT,
 * and there are two of them.
 *
 * Symmetric by construction: when the codes agree, one row; when they differ,
 * both get the same treatment. Resolving a description for only one slot would
 * name that slot, which is the oracle the blind exists to prevent.
 */
function SurgicalCell({ a, b }: { a: string; b: string }) {
  if (!a && !b) return <span className="text-muted-foreground">—</span>;
  // Agreement is the common case and reads better as one line than as the same
  // procedure printed twice.
  if (a === b || !b) return <SurgicalCode code={a} />;
  if (!a) return <SurgicalCode code={b} />;
  return (
    <span className="flex flex-col gap-1">
      <SurgicalCode code={a} />
      <SurgicalCode code={b} />
    </span>
  );
}

/**
 * How the two coded results compare — or that they cannot be compared.
 *
 * This is the defect it exists to fix: the cell rendered a discrepancy count of
 * zero as a green "Full match", and the live source hardcoded that count to
 * zero. So an audit queue asserted that the AI and the human agreed completely
 * on every case in the study, having compared none of them. A reviewer acting
 * on that would skip cases believing they were already settled.
 *
 * "Comparable" is what separates the two zeros. Most live cases have only one
 * side — FA populates CODER_ID on roughly a tenth of episodes at ingest — and
 * that is not agreement, it is nothing to agree about.
 *
 * The denominator comes from the SERVER, not from the local field list, so the
 * ratio cannot drift into one neither side agreed to.
 */
function AgreementBadge({ case: c }: { case: AuditBatchCaseSummary }) {
  if (!c.comparable) {
    return (
      <Badge
        variant="outline"
        title="Only one side has been coded, so there is nothing to compare yet."
      >
        Awaiting coder
      </Badge>
    );
  }
  if (c.discrepancyCount > 0) {
    return (
      <Badge variant="error">
        {c.discrepancyCount} of {c.comparedFieldCount}
      </Badge>
    );
  }
  return <Badge variant="success">Full match</Badge>;
}

/**
 * Distinguishes "you may not see this" from "we do not have this".
 *
 * The server omits the patient block entirely when the caller's grant does not
 * confer PHI, so an absent block is a PERMISSIONS outcome. Rendering it the same
 * as missing data would have a reviewer route a case as incomplete when the
 * record is complete and simply not theirs to read.
 */
function Restricted() {
  return (
    <span
      className="text-[11.5px] italic text-muted-foreground"
      title="Your access does not include patient identifiers for this case."
    >
      Restricted
    </span>
  );
}

/** Patient last name over MRN. No first name and no date of birth: both arrive
 *  redacted from FA and the server has no field for either. */
function PatientCell({ case: c }: { case: AuditBatchCaseSummary }) {
  if (!c.patient_context) return <Restricted />;
  const name = c.patient_context.lastName;
  if (!name && !c.patient_context.mrn) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="flex flex-col leading-tight">
      <span className="text-foreground">{name || "—"}</span>
      {c.patient_context.mrn && (
        <span className="font-mono text-[10.5px] text-muted-foreground">
          {c.patient_context.mrn}
        </span>
      )}
    </span>
  );
}

function BatchDetailBody({ ctx }: { ctx: BladeContext }) {
  const { status, data } = useAuditBatch(ctx.descriptor.entityId);
  // Sort and grouping, declared BEFORE the early returns so the hook order is
  // stable across loading, error and loaded renders.
  //
  // Case order is the reviewer's stated default: the identifier, ascending —
  // the order they read the list in, and stable across reloads because it is
  // derived from the data rather than from arrival.
  const [sortField, setSortField] = useState<CaseField>("case");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [groupField, setGroupField] = useState<CaseGroupField | "none">("none");

  // Walkthrough navigation. Registered BEFORE the early returns so the hook
  // order is stable across loading, error and loaded renders — and so the tour
  // is not left without a navigator while this blade is still fetching.
  useTourNavigator((screen) => {
    if (screen === "batch") return true;
    if (screen === "queue") {
      ctx.close();
      return true;
    }
    if (screen === "review") {
      const first = data?.cases[0];
      if (!first) return false;
      openReview(ctx, first);
      return true;
    }
    return false;
  });

  if (status === "loading") return <BladeLoading />;
  if (status === "error")
    return <BladeError message="Something went wrong loading this batch." />;
  if (status === "denied")
    return <BladeDenied description="You don’t have access to this batch." />;
  if (!data)
    return (
      <BladeEmpty
        title="Batch not found"
        description="This batch is no longer available."
      />
    );

  const batch: AuditBatch = data;
  // Fold in cases submitted during this session (verdict drafts are the demo store)
  // so the badges, reviewed count, Start/Continue verb, and completion banner all
  // advance as the auditor works through the batch. Read on each render — the blade
  // remounts when the focus stack returns here after a submit.
  //
  // This union is only honest because a disposition is written ONLY after the
  // server has accepted the submit (see finalize in auditCompare). While the
  // submit was fire-and-forget, a rejected review still landed here, and this
  // count reported the batch complete over work the server had never seen.
  const submitted = loadSubmittedIds();
  const effStatus = (c: AuditBatchCaseSummary): CaseStatus =>
    c.status === "submitted" || submitted.has(c.id)
      ? "submitted"
      : "unreviewed";
  const reviewedCount = batch.cases.filter(
    (c) => effStatus(c) === "submitted",
  ).length;
  const totalDiffs = batch.cases.reduce(
    (n, c) => n + (c.comparable && c.discrepancyCount > 0 ? 1 : 0),
    0,
  );
  // How many could be compared at all. Without this the header reports "0 cases
  // with discrepancies" over a batch where nothing was comparable, which reads
  // as a clean batch rather than an unmeasured one.
  const comparableCount = batch.cases.filter((c) => c.comparable).length;

  // Sorted once, then grouped from the ALREADY-SORTED list, so cases keep their
  // order inside a group rather than reverting to arrival order — and so the
  // groups themselves follow the sort. Same arrangement the batch list uses.
  const sorted = sortCases(batch.cases, sortField, sortDir);
  const groups = groupField === "none" ? null : groupCases(sorted, groupField);

  // "First" means first in the order the reviewer is LOOKING at, so Start
  // review opens the case at the top of their list. Taken from the unsorted
  // array it would open one they cannot see, which reads as the button jumping
  // to an arbitrary case — the fault sorting introduces if nothing follows it.
  const firstUnreviewed = sorted.find((c) => effStatus(c) !== "submitted");
  const complete = !firstUnreviewed;
  const progressTone = complete ? "teal" : "amber";

  // Clicking a header sorts by it; clicking the active one reverses.
  //
  // First click on a NEW column always starts ascending rather than inheriting
  // the previous column's direction, which would silently show a descending
  // list under a header the reviewer just picked.
  const toggleSort = (field: CaseField) => {
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDir("asc");
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Sticky so a long case list cannot scroll away the answer to "which
          batch am I in". An explicit background is required, not decoration:
          without it the rows show through the pinned header as they pass under
          it. */}
      <div className="sticky top-0 z-10 -mx-1 flex flex-col gap-4 bg-background px-1 pb-3 pt-1">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => ctx.close()}
            title="Back to the batch queue"
          >
            <ChevronLeft /> All batches
          </Button>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <BatchNumber
                value={batch.batchNumber}
                className="text-xl font-semibold"
              />
              <JobCodeChip code={batch.jobCode} />
              <BatchStateBadge state={batch.state} />
            </div>
            <p className="text-sm font-semibold text-foreground">
              {batch.client}
            </p>
            {/* No facility: it is per EPISODE and a batch may span several, so
                the batch-level value was always empty and this line opened with
                a stray separator. */}
            <p className="text-xs text-muted-foreground">
              {batch.dos && `${batch.dos} · `}
              {batch.caseCount}{" "}
              {batch.caseCount === 1 ? "case" : "cases"} · {reviewedCount} of{" "}
              {batch.caseCount} reviewed · {totalDiffs}{" "}
              {totalDiffs === 1 ? "case" : "cases"} with discrepancies
              {comparableCount < batch.caseCount &&
                ` · ${comparableCount} of ${batch.caseCount} comparable`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex w-40 flex-col gap-1">
              <span className="text-[11px] text-muted-foreground">
                {reviewedCount} / {batch.caseCount} reviewed
              </span>
              <Progress
                value={reviewedCount}
                max={batch.caseCount}
                tone={progressTone}
              />
            </div>
            {firstUnreviewed ? (
              <Button
                data-tour="tour-startreview"
                variant="primary"
                size="sm"
                onClick={(e) => openReview(ctx, firstUnreviewed, e)}
              >
                <Play />{" "}
                {reviewedCount > 0 ? "Continue review" : "Start review"}
              </Button>
            ) : (
              <Badge variant="success">Batch complete</Badge>
            )}
          </div>
        </div>
      </div>

      {complete && (
        <div
          className="flex items-center gap-3.5 rounded-coronis border px-4 py-3.5"
          style={{
            borderColor:
              "color-mix(in srgb, var(--ch-teal-500) 35%, transparent)",
            background:
              "linear-gradient(96deg, color-mix(in srgb, var(--ch-teal-500) 16%, var(--bg-surface)), var(--bg-surface) 72%)",
          }}
        >
          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-full text-white"
            style={{
              background: "var(--ch-teal-600, var(--ch-teal-500))",
              boxShadow:
                "0 0 0 5px color-mix(in srgb, var(--ch-teal-500) 14%, transparent)",
            }}
          >
            <Check className="size-5" />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="text-[13.5px] font-semibold text-coronis-teal-700 dark:text-coronis-teal-300">
              Batch reviewed — nice work
            </span>
            <span className="text-[11.5px] text-muted-foreground">
              All {batch.caseCount} {batch.caseCount === 1 ? "case" : "cases"}{" "}
              submitted · {totalDiffs} {totalDiffs === 1 ? "case" : "cases"}{" "}
              with discrepancies flagged for the {batch.client} report.
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => ctx.close()}
          >
            Back to batches <ChevronRight />
          </Button>
        </div>
      )}

      <CaseTableControls
        sortField={sortField}
        sortDir={sortDir}
        groupField={groupField}
        onSort={(f, d) => {
          setSortField(f);
          setSortDir(d);
        }}
        onGroup={setGroupField}
      />

      {groups ? (
        <div data-tour="tour-caselist" className="flex flex-col gap-5">
          {groups.map((g) => (
            <section key={g.key} className="flex flex-col gap-2">
              <header className="flex items-baseline gap-2 border-b border-border pb-1">
                <h3 className="text-sm font-semibold text-foreground">
                  {g.label}
                </h3>
                {/* An exact count. Unlike the batch list's coder grouping, this
                    is a true partition — a case has one coder, one status and
                    one facility — so the group totals sum to the batch. */}
                <span className="text-xs text-muted-foreground">
                  {g.cases.length} {g.cases.length === 1 ? "case" : "cases"}
                </span>
              </header>
              <CaseTable
                cases={g.cases}
                ctx={ctx}
                effStatus={effStatus}
                sortField={sortField}
                sortDir={sortDir}
                onToggleSort={toggleSort}
              />
            </section>
          ))}
        </div>
      ) : (
        <div data-tour="tour-caselist">
          <CaseTable
            cases={sorted}
            ctx={ctx}
            effStatus={effStatus}
            sortField={sortField}
            sortDir={sortDir}
            onToggleSort={toggleSort}
          />
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {complete
          ? "Every case in this batch has been submitted."
          : `Open a case to review it side by side. ${batchActionLabel(batch.state)} with “${
              reviewedCount > 0 ? "Continue review" : "Start review"
            }”.`}
      </p>
    </div>
  );
}

/**
 * The sort and group controls, mirroring the batch list's.
 *
 * Both a dropdown and clickable column headers, because they answer different
 * needs: the dropdown names every column in one place (and says which way round
 * it is sorting), while a header click is what a reviewer reaches for once they
 * can see the column they want. The dropdown is also the only route on a narrow
 * screen, where the table scrolls sideways and the header may be off-screen.
 *
 * Driven off CASE_COLUMNS so the two can never offer different columns.
 */
function CaseTableControls({
  sortField,
  sortDir,
  groupField,
  onSort,
  onGroup,
}: {
  sortField: CaseField;
  sortDir: SortDirection;
  groupField: CaseGroupField | "none";
  onSort: (field: CaseField, dir: SortDirection) => void;
  onGroup: (field: CaseGroupField | "none") => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        Group
        <select
          className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
          value={groupField}
          onChange={(e) => onGroup(e.target.value as CaseGroupField | "none")}
          aria-label="Group cases by"
        >
          <option value="none">None</option>
          {CASE_GROUP_FIELDS.map((g) => (
            <option key={g.field} value={g.field}>
              {g.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        Sort
        <select
          className="h-8 rounded-coronis border border-border bg-card px-2 text-xs text-foreground"
          value={`${sortField}:${sortDir}`}
          onChange={(e) => {
            const [f, d] = e.target.value.split(":");
            onSort(f as CaseField, d as SortDirection);
          }}
          aria-label="Sort cases by"
        >
          {CASE_COLUMNS.filter((col) => col.sortable).flatMap((col) => [
            <option key={`${col.field}:asc`} value={`${col.field}:asc`}>
              {col.label}, ascending
            </option>,
            <option key={`${col.field}:desc`} value={`${col.field}:desc`}>
              {col.label}, descending
            </option>,
          ])}
        </select>
      </label>
    </div>
  );
}

/** A sortable column header. Renders the direction only on the ACTIVE column —
 *  an arrow on every header says nothing about which one is in force. */
function CaseHeader({
  column,
  sortField,
  sortDir,
  onToggle,
}: {
  column: CaseColumn;
  sortField: CaseField;
  sortDir: SortDirection;
  onToggle: (field: CaseField) => void;
}) {
  const active = column.field === sortField;
  const className = `px-3 py-2 font-medium ${column.align === "center" ? "text-center" : ""}`;
  if (!column.sortable) {
    return (
      <th className={className} title={column.title}>
        {column.label}
      </th>
    );
  }
  return (
    <th
      className={className}
      // aria-sort is what tells a screen reader which column orders the table
      // and which way; the arrow glyph below is invisible to one.
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      title={column.title}
    >
      <button
        type="button"
        onClick={() => onToggle(column.field)}
        className={`flex items-center gap-1 font-medium uppercase tracking-[0.04em] transition-colors hover:text-foreground ${
          active ? "text-foreground" : ""
        } ${column.align === "center" ? "mx-auto" : ""}`}
      >
        {column.label}
        {active ? (
          <ArrowUp
            className={`size-3 shrink-0 transition-transform ${sortDir === "desc" ? "rotate-180" : ""}`}
            aria-hidden
          />
        ) : (
          // A dimmed placeholder on every sortable header, so the columns do
          // not shift sideways as the active one changes.
          <ArrowUp className="size-3 shrink-0 opacity-0" aria-hidden />
        )}
      </button>
    </th>
  );
}

function CaseTable({
  cases,
  ctx,
  effStatus,
  sortField,
  sortDir,
  onToggleSort,
}: {
  cases: AuditBatchCaseSummary[];
  ctx: BladeContext;
  effStatus: (c: AuditBatchCaseSummary) => CaseStatus;
  sortField: CaseField;
  sortDir: SortDirection;
  onToggleSort: (field: CaseField) => void;
}) {
  return (
    <div className="overflow-auto rounded-coronis border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
            {/* Service date and facility replace the old Procedure column.
                FA's flattened episode query carries ids and codes, never a
                procedure narrative, so that column could only ever be blank —
                and the surgical CPT teaser two columns over is the closest
                honest answer to what was done. */}
            {CASE_COLUMNS.map((col) => (
              <CaseHeader
                key={col.field}
                column={col}
                sortField={sortField}
                sortDir={sortDir}
                onToggle={onToggleSort}
              />
            ))}
            <th className="w-[90px] px-3 py-2" />
          </tr>
        </thead>
        <tbody>
            {cases.map((c) => {
              // coderIdentity, not coderById: an unresolved FA id must not
              // render as a real person from the demo roster.
              //
              // The name rides on the episode context, which is where FA's coder
              // identity lives. Absent when the coder leg has not arrived, or when
              // the id did not resolve — either way the id is the fallback, never a
              // borrowed name.
              const coder = coderIdentity(c.coderId, c.episode?.coderName);
              return (
                <tr
                  key={c.id}
                  onClick={(e) => openReview(ctx, c, e)}
                  className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-muted"
                >
                  {/* FA's episode id, or the DCN tagged as such — see
                      CaseIdentifier. The two are both bare digit strings in
                      production, so an untagged fallback would put two
                      indistinguishable kinds of number in one column. */}
                  <td className="px-3 py-2.5 text-xs font-semibold text-foreground">
                    <CaseIdentifier
                      value={caseLabel(c)}
                      isFallback={isFallbackLabel(c)}
                      title={caseLabelTitle(c)}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <PatientCell case={c} />
                  </td>
                  {/* FORMATTED. FA's date columns are strings and arrive as ISO
                      timestamps, which this rendered verbatim. */}
                  <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">
                    {faDate(c.episode?.serviceStartDate) || "—"}
                  </td>
                  <td className="max-w-[140px] px-3 py-2.5">
                    <span className="block truncate text-foreground">
                      <FacilityLabel
                        facilityId={c.episode?.facilityId ?? ""}
                        facilityName={c.episode?.facilityName ?? ""}
                      />
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {c.coderId ? (
                      <span className="flex items-center gap-2 whitespace-nowrap">
                        <CoderAvatar coder={coder} /> {coder.name}
                      </span>
                    ) : (
                      // FA populates CODER_ID on roughly a tenth of rows at
                      // ingest — human coding lags episode creation. "Not coded
                      // yet" is the fact; an avatar here would invent a person.
                      <span className="whitespace-nowrap text-[11.5px] text-muted-foreground">
                        Not coded yet
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-foreground">
                    {c.patient_context ? c.payer || "—" : <Restricted />}
                  </td>
                  <td className="px-3 py-2.5">
                    <SurgicalCell a={c.surgical.a} b={c.surgical.b} />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <AgreementBadge case={c} />
                  </td>
                  <td className="px-3 py-2.5">
                    <CaseStatusBadge status={effStatus(c)} />
                  </td>
                  <td className="px-3 py-2.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => openReview(ctx, c, e)}
                    >
                      {effStatus(c) === "submitted" ? "View" : "Review"}{" "}
                      <ChevronRight />
                    </Button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

export const auditBatchPrimitive: ViewPrimitive = {
  key: "audit-batch",
  defaultTitle: "Batch",
  defaultEyebrow: "Code evaluation",
  defaultWidth: "xwide",
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === "loading") return <BladeLoading />;
    if (forced === "error") return <BladeError />;
    if (forced === "denied") return <BladeDenied />;
    if (forced === "empty")
      return (
        <BladeEmpty
          title="Batch not found"
          description="This batch is no longer available."
        />
      );
    return <BatchDetailBody ctx={ctx} />;
  },
};
